import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { StorageService } from "@core/services/storage/storage.service";
import { CloudFolder } from "@shared/models/file-types";
import { InAppBrowser } from "@awesome-cordova-plugins/in-app-browser/ngx";
import { environment } from "@env";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { NavController, ToastController } from "@ionic/angular";
import { DropBoxPayload } from "@app/shared/models/DropBoxPayload";
import { LoadingService } from "../services/loading/loading.service";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class Dropbox {
  accessToken: any;
  folderHistory: CloudFolder[] = [];

  redirectURI = environment.redirectBaseUrl + "dropbox";
  url: any;

  constructor(
    private httpClient: HttpClient,
    private iab: InAppBrowser,
    private storageSvc: StorageService,
    private navCtrl: NavController,
    private loadingSvc: LoadingService,
    private translateService: TranslateService,
    private toastCtrl: ToastController
  ) {
    this.url =
      "https://www.dropbox.com/oauth2/authorize?client_id=" +
      environment.droboxAppKey +
      "&redirect_uri=" +
      this.redirectURI +
      "&token_access_type=offline&prompt=consent&response_type=code&scope=file_requests.read files.metadata.read files.content.read";
  }

  setAccessToken(token) {
    this.accessToken = token;
  }

  getUserInfo() {
    const headers = new HttpHeaders();

    headers.append("Authorization", "Bearer " + this.accessToken);
    headers.append("Content-Type", "application/json");
    return this.httpClient
      .post(
        "https://api.dropboxapi.com/2-beta-2/users/get_current_account",
        "null",
        { headers }
      )
      .toPromise();
  }

  async downloadFile(filePath): Promise<any> {
    // const httpOptions: HttpOptions = {
    //   url: "https://api.dropboxapi.com/2/files/get_temporary_link",
    //   method: "Post",
    //   headers: {
    //     "Content-Type": "text/plain; charset=dropbox-cors-hack",
    //     Authorization: "Bearer " + this.accessToken,
    //     responseType: "blob",
    //   },
    //   data: JSON.stringify({ path: filePath }),
    // };

    // const response = await Http.post(httpOptions);
    // return await this.httpClient
    //   .get(response.data.link, { responseType: "blob" })
    //   .toPromise();

    // const httpOptions = {
    //   url: "https://api.dropboxapi.com/2/files/get_temporary_link",
    //   method: "Post",
    //   headers: {
    //     "Content-Type": "text/plain; charset=utf-8",
    //     Authorization: "Bearer " + this.accessToken,
    //     "Dropbox-API-Arg": JSON.stringify({ path: "id:" + filePath }),
    //   },
    // };
    // return this.httpClient
    //   .post('https://content.dropboxapi.com/2/files/download', null, {
    //     headers: {
    //       'Content-Type': 'text/plain; charset=utf-8',
    //       Authorization: 'Bearer ' + this.accessToken,

    //       'Dropbox-API-Arg': JSON.stringify({ path:  filePath }),
    //     },
    //   })
    //   .toPromise();
    return new Promise<any>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://content.dropboxapi.com/2/files/download", true);
      xhr.setRequestHeader("Authorization", "Bearer " + this.accessToken);
      xhr.setRequestHeader(
        "Dropbox-API-Arg",
        JSON.stringify({ path: filePath })
      );
      xhr.responseType = "arraybuffer";
      xhr.onload = async () => {
        resolve(xhr.response);
      };
      xhr.send();
    });
  }

  getFolders(folder: CloudFolder): Promise<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: "Bearer " + this.accessToken,
      }),
    };

    let folderPath;

    if (folder.id === "root") {
      folderPath = {
        path: "",
      };
    } else {
      folderPath = {
        path: folder.id,
      };
    }
    this.pushToHistory(folder);

    return this.httpClient
      .post(
        "https://api.dropboxapi.com/2/files/list_folder",
        JSON.stringify(folderPath),
        httpOptions
      )
      .toPromise();
  }

  pushToHistory(folder: CloudFolder) {
    const history = this.folderHistory;

    const i = history.findIndex((e) => e.id === folder.id);

    if (i >= 0) {
      this.folderHistory = history.slice(0, i);
    }

    this.folderHistory.push(folder);
  }

  login() {
    return new Promise(async (resolve, reject) => {
      const browserType = Capacitor.isNativePlatform() ? "_system" : "_self";
      this.iab.create(this.url, browserType, "location=no");
      resolve(true);
    });
  }

  async dropBoxLoginHandler(redirectUrl: string) {
    try {
      let tokenPayload = await this.getTokenFromCode(redirectUrl);
      if (tokenPayload) {
        const token = this.saveToken(tokenPayload["data"] as DropBoxPayload);
        this.setAccessToken(token);
        await this.navCtrl.navigateRoot("app/add-files");
        this.navCtrl.navigateForward("/app/add-files/dropbox-list");
      }
    } catch (error) {
      const toast = await this.toastCtrl.create({
        message: this.translateService.instant("COULD_NOT_AUTHENTICATE"),
        color: "danger",
        duration: 2000,
      });
      toast.present();
      await this.navCtrl.navigateRoot("app/add-files");
    }
  }
  saveToken(tokenPayload: DropBoxPayload): string {
    tokenPayload.expires = this.currentTimestamp() + tokenPayload.expires_in;
    this.storageSvc.setJson("dropbox", tokenPayload);
    this.setAccessToken(tokenPayload.access_token);
    return tokenPayload.access_token;
  }

  async getTokenFromCode(redirectUrl: string) {
    return new Promise(async (resolve, reject) => {
      try {
        const url_parsed = redirectUrl.split("code=");
        const code = url_parsed[1];
        const url =
          "https://www.dropbox.com/oauth2/token?grant_type=authorization_code&client_secret=" +
          environment.droboxAppSecret +
          "&client_id=" +
          environment.droboxAppKey +
          "&redirect_uri=" +
          this.redirectURI +
          "&code=" +
          code;

        const options = {
          url,
        };
        let authResp = await CapacitorHttp.post(options);

        resolve(authResp);
      } catch (error) {
        reject(error);
      }
    });
  }

  async getToken() {
    const obj: DropBoxPayload = await this.getUserFromStorage();
    if (obj) {
      if (obj.expires > this.currentTimestamp()) {
        return obj.access_token;
      } else {
        return this.refreshAccessToken(obj);
      }
    }
    return null;
  }
  async getUserFromStorage(): Promise<DropBoxPayload> {
    let user = (await this.storageSvc.getJson("dropbox")) as DropBoxPayload;
    return user;
  }
  async refreshAccessToken(oldPayload: DropBoxPayload) {
    return new Promise<string>(async (resolve, reject) => {
      try {
        await this.loadingSvc.present("Please wait...");
        const url =
          "https://www.dropbox.com/oauth2/token?grant_type=refresh_token&client_secret=" +
          environment.droboxAppSecret +
          "&client_id=" +
          environment.droboxAppKey +
          "&refresh_token=" +
          oldPayload.refresh_token;

        const options = {
          url,
        };
        let refreshTokenResp = await CapacitorHttp.post(options);
        let refreshTokenPayload = refreshTokenResp.data;

        oldPayload.access_token = refreshTokenPayload["access_token"];
        oldPayload.expires_in = refreshTokenPayload["expires_in"];

        this.saveToken(oldPayload);
        await this.loadingSvc.present(
          this.translateService.instant("COULD_NOT_AUTHENTICATE")
        );
        resolve(oldPayload.access_token);
      } catch (error) {
        reject(error);
      }
    });
  }
  private currentTimestamp(): number {
    return Math.floor(new Date().getTime() / 1000);
  }
}
